import React from "react";
import styled from "styled-components";
import { Box, Divider, Stack, Text } from "@origin-digital/ods-core";
import { IconDescription } from "@origin-digital/ods-icons";
import type { CategoryArticleItem } from "../../../services/categories/types";
import { CustomLink } from "../../../components/CustomLink";
import { CategoryFallback } from "./CategoryFallback";

interface SubcategoryArticleListProps {
  articles: CategoryArticleItem[];
  "data-id"?: string;
}

const ArticleLink = styled(CustomLink)`
  display: block;
  width: 100%;
  color: inherit;
  text-decoration: none;

  &:hover,
  &:focus,
  &:visited,
  &:active {
    color: inherit;
    text-decoration: none;
  }
`;

const ArticleTitle = styled(Text)`
  ${ArticleLink}:hover & {
    text-decoration: underline;
  }
`;

// styled.p instead of styled(Text): -webkit-line-clamp must apply to the element
// containing the text directly; styled(Text) attaches the class higher up the tree
// and the clamp is defeated by Text's internal element styles. Mirrors the working
// pattern in SearchResultItem.tsx. Font-size 14px matches QA spec + ODS `xxsmall`.
const ClampedExcerpt = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => props.theme.colors.neutral};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  align-items: flex-start;
  margin-right: 16px;
`;

const ArticleTile: React.FC<{ article: CategoryArticleItem }> = ({
  article,
}) => (
  <Box paddingX="medium">
    <ArticleLink
      href={article.url}
      data-id={`subcategory-article-link-${article.slug}`}
    >
      <Box
        display="flex"
        flexDirection="row"
        alignItems="flex-start"
        gap="small"
        paddingY="large"
        marginRight="medium"
      >
        <Box paddingY="xxxsmall">
          <IconDescription color="neutral" size="xxsmall" />
        </Box>
        <Stack space="xxsmall">
          <ArticleTitle
            variant="subtitle-small"
            weight="medium"
            tone="neutralDark"
          >
            {article.title}
          </ArticleTitle>
          {article.excerpt && (
            <ClampedExcerpt>{article.excerpt}</ClampedExcerpt>
          )}
        </Stack>
      </Box>
    </ArticleLink>
    <Divider />
  </Box>
);

export const SubcategoryArticleList: React.FC<SubcategoryArticleListProps> = ({
  articles,
  "data-id": dataId = "subcategory-article-list",
}) => {
  const validArticles = articles.filter(
    (article) => article.title && article.url && article.slug
  );

  if (validArticles.length === 0) {
    return <CategoryFallback />;
  }

  return (
    <Stack space="none" data-id={dataId}>
      {validArticles.map((article) => (
        <ArticleTile key={article.id} article={article} />
      ))}
    </Stack>
  );
};
